import type { WebsocketRequest } from '@talos/kyoko';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MARKET_QUOTE,
  MixpanelEvent,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMarketQuoteColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type MarketQuote,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { useMarketQuotesFilter, type MarketQuotesTableFilter } from './useMarketQuotesFilter';
import { useMarketQuotesMenu } from './useMarketQuotesMenu';

export interface FilteredMarketQuotesParams {
  blotterID: string;
  tabLabel?: string;
  defaultColumns: Column[];
  defaultFilter: MarketQuotesTableFilter;
  initialIsOpen?: boolean;

  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<MarketQuote>) => void;
}

export function MarketQuotesBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: FilteredMarketQuotesParams) {
  const mixpanel = useMixpanel();
  const columns = useMarketQuoteColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useMarketQuotesFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const marketQuotesMenu = useMarketQuotesMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const columnsWithMenu = useMemo(() => [...columns, ...marketQuotesMenu.columns], [columns, marketQuotesMenu.columns]);

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: MARKET_QUOTE,
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, MarketQuote>({
    request,
    rowID: 'QID',
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    contextMenuOptions: {
      getCustomContextMenuItems: marketQuotesMenu.getCustomContextMenuItems,
    },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'MarketQuotes',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {marketQuotesMenu.dialogs}
    </>
  );
}

function onlyServerFilterKeys(filter: MarketQuotesTableFilter | undefined) {
  if (!filter) {
    return filter;
  }
  const serverFilter = pick(filter, ['StartDate', 'EndDate', 'Symbols', 'Statuses', 'ParentRFQID']);
  return serverFilter;
}
